import React, { useState, useEffect, Fragment, useMemo } from "react";
import { MdOutlineSubdirectoryArrowRight } from "react-icons/md";
import Pagination from "../../helpers/Pagination";
import RowAction from "./RowAction";

const ModernTable = ({
  columns = [],
  data = [],
  actions = [],
  selectable = true,
  selectedRows = [], // Array of checked row IDs
  onSelectionChange, // V1 Callback: returns updated array of IDs
  onSelectRow,       // V2 Callback: toggles a single row
  onSelectAll,       // V2 Callback: toggles all rows
  pagination,
  showHeader = true,
  actionType = "dropdown",
  expandable = false,
  expandedRow = null,
  onRowClick,
  renderExpandedRow,
  expandedColumns = [],
  stickyColumns = 0,
  isRowSelectable = () => true, // V1 feature: filter which rows can be checked
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const effectiveSticky = isMobile ? 0 : stickyColumns;

  // Computed list of only selectable row IDs based on the filter (V1 Feature)
  const selectableRowIds = useMemo(() => {
    return data
      .filter((row) => isRowSelectable(row))
      .map((row) => row._id || row.id);
  }, [data, isRowSelectable]);

  // Check if all selectable rows are checked (V1 Logic)
  const isAllSelected =
    selectableRowIds.length > 0 &&
    selectableRowIds.every((id) => selectedRows.includes(id));

  // Combined Select All Handler
  const handleHeaderCheckboxChange = (e) => {
    if (onSelectAll) {
      // If V2 prop is provided, use it
      onSelectAll(e);
    } else if (onSelectionChange) {
      // Otherwise use V1 logic
      if (e.target.checked) {
        onSelectionChange(selectableRowIds);
      } else {
        onSelectionChange([]);
      }
    }
  };

  // Combined Single Row Handler
  const handleRowCheckboxChange = (rowKey) => {
    if (onSelectRow) {
      // Use V2 logic
      onSelectRow(rowKey);
    } else if (onSelectionChange) {
      // Use V1 logic
      if (selectedRows.includes(rowKey)) {
        onSelectionChange(selectedRows.filter((id) => id !== rowKey));
      } else {
        onSelectionChange([...selectedRows, rowKey]);
      }
    }
  };

  return (
    <div className="w-full">
      <div className="w-full overflow-x-auto custom-scrollbar">
        <table className="w-full border-separate border-spacing-y-[10px] text-sm">
          {/* ---------- HEADER ---------- */}
          {showHeader && (
            <thead>
              <tr className="bg-primary text-gray-100 text-sm">
                {selectable && (
                  <th className="px-4 h-[64px] align-middle rounded-l-xl">
                    <div className="flex items-center justify-center h-full">
                      <input
                        type="checkbox"
                        checked={!!isAllSelected}
                        onChange={handleHeaderCheckboxChange}
                        className="h-4 w-4 accent-indigo-600 cursor-pointer"
                      />
                    </div>
                  </th>
                )}

                {columns.map((col, index) => {
                  const isFirst = index === 0;
                  const isLast = index === columns.length - 1 && actions.length === 0;
                  const isSticky = index < effectiveSticky;
                  const leftValue = index * 160;

                  return (
                    <th
                      key={col.key || index}
                      className={`px-4 h-[64px] font-medium align-middle
                        ${col.headerAlign === "center" ? "text-center" : col.headerAlign === "right" ? "text-right" : "text-left"}
                        ${!selectable && isFirst ? "rounded-l-xl" : ""}
                        ${isLast ? "rounded-r-xl" : ""}
                        ${isSticky ? "sticky left-0 bg-primary z-30 shadow-[2px_0_2px_rgba(0,0,0,0.02)]" : ""}
                      `}
                      style={isSticky ? { left: `${leftValue}px` } : {}}
                    >
                      <div className={`flex items-center h-full whitespace-nowrap ${col.headerAlign === "center" ? "justify-center" : col.headerAlign === "right" ? "justify-end" : "justify-start"}`}>
                        {col.label}
                      </div>
                    </th>
                  );
                })}

                {actions.length > 0 && (
                  <th className="px-4 h-[64px] text-right align-middle rounded-r-xl">
                    <div className="flex items-center justify-end h-full">
                      {actionType === "button" ? "Operations" : "Action"}
                    </div>
                  </th>
                )}
              </tr>
            </thead>
          )}

          {/* ---------- BODY ---------- */}
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions.length ? 1 : 0)}
                  className="text-center py-16 text-slate-500 bg-white rounded-xl"
                >
                  No data found
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowKey = row._id || row.id || `row-${rowIndex}`;
                const isSelected = selectedRows.includes(rowKey);
                const isExpanded = expandedRow === rowKey;

                return (
                  <Fragment key={rowKey}>
                    <tr
                      className={`bg-white transition ${expandable ? "cursor-pointer hover:bg-gray-50" : "hover:bg-[#def3ea]"}`}
                      onClick={() => expandable && onRowClick?.(row)}
                    >
                      {/* Selection Checkbox */}
                      {selectable && (
                        <td className="px-4 h-[64px] align-middle rounded-l-xl">
                          <div className="flex items-center justify-center h-full">
                            {isRowSelectable(row) ? (
                              <input
                                type="checkbox"
                                checked={!!isSelected}
                                onChange={(e) => {
                                  e.stopPropagation(); // Prevent row click when clicking checkbox
                                  handleRowCheckboxChange(rowKey);
                                }}
                                className="h-4 w-4 accent-indigo-600 cursor-pointer"
                              />
                            ) : (
                              <input
                                type="checkbox"
                                disabled
                                className="h-4 w-4 accent-slate-200 cursor-not-allowed opacity-30"
                              />
                            )}
                          </div>
                        </td>
                      )}

                      {/* Data Columns */}
                      {columns.map((col, index) => {
                        const isFirst = index === 0;
                        const isLast = index === columns.length - 1 && actions.length === 0;
                        const isSticky = index < effectiveSticky;
                        const leftValue = index * 160;

                        return (
                          <td
                            key={col.key || index}
                            className={`px-4 h-[64px] text-sm align-middle
                              ${isSticky ? "sticky bg-white z-10 shadow-[2px_0_2px_rgba(0,0,0,0.02)]" : ""}
                              ${!selectable && isFirst ? "rounded-l-xl" : ""}
                              ${isLast ? "rounded-r-xl" : ""}
                              ${col.wrap ? "whitespace-normal" : "whitespace-nowrap"}
                            `}
                            style={isSticky ? { left: `${leftValue}px` } : {}}
                          >
                            <div className="flex items-center h-full whitespace-nowrap">
                              {col.render ? col.render(row) : row[col.key]}
                            </div>
                          </td>
                        );
                      })}

                      {/* Actions Column */}
                      {actions.length > 0 && (
                        <td className="px-4 h-[64px] text-right align-middle rounded-r-xl">
                          <div className="flex items-center justify-end h-full">
                            {actionType === "button" ? (
                              <div className="flex gap-2">
                                {actions.map((action, i) => (
                                  <div key={i}>
                                    {action.render ? action.render(row) : null}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <RowAction actions={actions} row={row} />
                            )}
                          </div>
                        </td>
                      )}
                    </tr>

                    {/* ================= EXPANDED SECTION (V2 Feature) ================= */}
                    {expandable && isExpanded && (
                      <tr>
                        <td colSpan={columns.length + (selectable ? 1 : 0) + (actions.length ? 1 : 0)}>
                          <div className="flex items-start gap-2 pl-6 pb-2">
                            {/* Directional Arrow */}
                            <MdOutlineSubdirectoryArrowRight className="text-gray-400 mt-4 shrink-0" size={18} />

                            {/* Inner Nested Table */}
                            <div className="w-full overflow-x-auto">
                              {renderExpandedRow ? (
                                renderExpandedRow(row)
                              ) : (
                                <table className="w-full border-separate border-spacing-y-[10px] text-sm min-w-[500px]">
                                  <thead>
                                    <tr className="bg-secondary text-xs">
                                      {expandedColumns.map((col, idx) => (
                                        <th
                                          key={idx}
                                          className={`px-3 h-[52px] text-start ${idx === 0 ? "rounded-l-lg" : ""} ${idx === expandedColumns.length - 1 ? "rounded-r-lg" : ""}`}
                                        >
                                          {col.label}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.items?.map((item, itemIndex) => (
                                      <tr key={itemIndex} className="bg-white">
                                        {expandedColumns.map((col, colIndex) => (
                                          <td
                                            key={colIndex}
                                            className={`px-3 h-[52px] whitespace-nowrap ${colIndex === 0 ? "rounded-l-lg" : ""} ${colIndex === expandedColumns.length - 1 ? "rounded-r-lg" : ""}`}
                                          >
                                            {col.render
                                              ? col.render(item, row, itemIndex)
                                              : item[col.key]}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ---------- PAGINATION (V1 & V2) ---------- */}
      {pagination && data.length > 0 && (
        <Pagination
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          currentPage={pagination.currentPage}
          onPageChange={pagination.onPageChange}
          onItemsPerPageChange={pagination.onItemsPerPageChange}
        />
      )}
    </div>
  );
};

export default ModernTable;